import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import CategoryIcon from "@mui/icons-material/Category";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import EquiposDeModeloDialog from "../components/EquiposDeModeloDialog";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { fieldSx } from "../../egresos/components/formStyles";
import {
  crearFalla, crearModelo, crearServicio, crearTipoEquipo, crearTipoModelo, eliminarModelo, eliminarTipoEquipo, eliminarTipoModelo, getCatalogos, getTipoModelo, listarFallas, listarServicios,
  listarTiposEquipo, listarTiposModelo, mensajeError,
} from "../services/catalogoEquiposService";

const cabecera = { fontWeight: 800, fontSize: 11, whiteSpace: "nowrap", bgcolor: "primary.main", color: "primary.contrastText" };
const tablaSx = { "& .MuiTableCell-root": { py: 0.35, px: 1, fontSize: 12 }, "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" } };

// Tabla simple con cabecera fija y mensaje de vacío.
function Listado({ columnas, filas, vacio = "Sin registros.", resaltar = null }) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: "calc(100vh - 330px)" }}>
        <Table size="small" stickyHeader sx={tablaSx}>
          <TableHead>
            <TableRow>{columnas.map((c) => <TableCell key={c.campo} sx={cabecera} align={c.align}>{c.titulo}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {filas.length === 0 && <TableRow><TableCell colSpan={columnas.length}><Typography variant="body2" color="text.secondary">{vacio}</Typography></TableCell></TableRow>}
            {filas.map((f) => (
              <TableRow key={f.id} hover sx={f.id === resaltar ? { "&&": { bgcolor: "success.light" } } : undefined}>{columnas.map((c) => <TableCell key={c.campo} align={c.align}>{c.render ? c.render(f) : f[c.campo]}</TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// Formulario en línea: guarda, muestra el error del servidor y avisa al padre para recargar.
function useGuardar(onGuardado) {
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [guardando, setGuardando] = useState(false);
  const temporizador = useRef(null);
  useEffect(() => () => clearTimeout(temporizador.current), []);

  // El mensaje se muestra como notificación y se oculta solo.
  const avisar = (tipo, texto, ms) => {
    clearTimeout(temporizador.current);
    setAviso(tipo === "ok" ? texto : "");
    setError(tipo === "error" ? texto : "");
    temporizador.current = setTimeout(() => { setAviso(""); setError(""); }, ms);
  };

  const guardar = async (accion, mensaje) => {
    setGuardando(true);
    try {
      await accion();
      avisar("ok", mensaje, 3500);
      await onGuardado();
      return true;
    } catch (e) {
      avisar("error", mensajeError(e, "No se pudo guardar."), 6000);
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Pide confirmación y, si el usuario acepta, guarda. Devuelve true solo si se guardó.
  const [pendiente, setPendiente] = useState(null);
  const confirmar = (titulo, detalle, accion, mensaje, opciones = {}) => new Promise((resolve) => {
    setPendiente({
      titulo,
      detalle,
      ...opciones,
      responder: async (acepta) => {
        setPendiente(null);
        resolve(acepta ? await guardar(accion, mensaje) : false);
      },
    });
  });
  const dialogo = pendiente && (
    <Dialog open onClose={() => pendiente.responder(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>{pendiente.titulo}</DialogTitle>
      <DialogContent><DialogContentText sx={{ fontSize: 14 }}>{pendiente.detalle}</DialogContentText></DialogContent>
      <DialogActions>
        <Button onClick={() => pendiente.responder(false)}>Cancelar</Button>
        <Button variant="contained" color={pendiente.color || "primary"} autoFocus onClick={() => pendiente.responder(true)}>{pendiente.textoConfirmar || "Sí, registrar"}</Button>
      </DialogActions>
    </Dialog>
  );

  return { error, aviso, guardando, guardar, confirmar, dialogo };
}

// Notificación flotante abajo al centro: no desplaza el contenido de la página.
const Mensajes = ({ error, aviso }) => {
  const texto = error || aviso;
  return (
    <Snackbar open={!!texto} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert severity={error ? "error" : "success"} variant="filled" sx={{ fontWeight: 600 }}>{texto}</Alert>
    </Snackbar>
  );
};

const AVISO_ACCESORIO = "Los accesorios se controlan solo por cantidad: al registrar equipos no se les asigna serie.";
const AvisoAccesorio = ({ sx }) => <Alert severity="info" sx={{ py: 0, fontSize: 12, ...sx }}>{AVISO_ACCESORIO}</Alert>;

const Barra = ({ children }) => <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap", alignItems: "flex-start" }}>{children}</Box>;

/* ------------------------------ Catálogo ------------------------------ */

// Cada columna depende de la anterior: Servicio → Tipo de equipo → Tipo de modelo → Modelo.
const ALTURA_COLUMNA = { xs: 340, md: "calc(100vh - 205px)" };

function Columna({ numero, titulo, ayuda, color = "primary", icono: Icono, bloqueada, mensajeBloqueada, children }) {
  return (
    <Paper variant="outlined" sx={{ display: "flex", flexDirection: "column", minWidth: 0, height: ALTURA_COLUMNA, minHeight: 320, overflow: "hidden", borderColor: (t) => alpha(t.palette[color].main, 0.4) }}>
      <Box sx={{ px: 1, py: 0.75, display: "flex", alignItems: "center", gap: 1, bgcolor: (t) => alpha(t.palette[color].main, 0.12), borderBottom: 1, borderColor: (t) => alpha(t.palette[color].main, 0.4) }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", bgcolor: `${color}.main`, color: `${color}.contrastText`, opacity: bloqueada ? 0.5 : 1 }}>
          {Icono && <Icono fontSize="small" />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: `${color}.main`, letterSpacing: 0.3 }}>{numero}. {titulo.toUpperCase()}</Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{ayuda}</Typography>
        </Box>
      </Box>
      {bloqueada
        ? <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>{mensajeBloqueada}</Typography>
        : children}
    </Paper>
  );
}

// Campo para registrar uno nuevo dentro de la columna: Enter o el botón "+" guardan.
function AgregarFila({ color = "primary", etiqueta, maxLength, existentes = [], guardando, onAgregar }) {
  const [valor, setValor] = useState("");
  const limpio = valor.trim().replace(/\s+/g, " ");
  const existe = !!limpio && existentes.includes(limpio.toLowerCase());
  const puede = !!limpio && !existe && !guardando;
  const enviar = async () => {
    if (!puede) return;
    if (await onAgregar(limpio)) setValor("");
  };
  return (
    <Box sx={{ p: 1, pb: 0.5 }}>
      <TextField
        fullWidth size="small" placeholder={etiqueta} value={valor} onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); enviar(); } }}
        error={existe} helperText={existe ? "Ya existe" : undefined} sx={fieldSx}
        slotProps={{
          htmlInput: { maxLength },
          input: { endAdornment: <InputAdornment position="end"><IconButton size="small" color={color} aria-label="Agregar" disabled={!puede} onClick={enviar}><AddIcon fontSize="small" /></IconButton></InputAdornment> },
        }}
      />
    </Box>
  );
}

// items: { key, texto, secundario, chip, etiqueta, resaltado }. Con onElegir son seleccionables; sin él, solo se leen.
function ListaItems({ items, seleccionado, onElegir, vacio, color = "primary", icono: Icono }) {
  const [filtro, setFiltro] = useState("");
  const f = filtro.trim().toLowerCase();
  const visibles = f ? items.filter((i) => i.texto.toLowerCase().includes(f)) : items;
  const contenido = (i) => (
    <>
      {Icono && <ListItemIcon sx={{ minWidth: 32, color: `${color}.main` }}><Icono fontSize="small" /></ListItemIcon>}
      <ListItemText primary={i.texto} secondary={i.secundario} slotProps={{ primary: { sx: { fontSize: 13, fontWeight: 600 } }, secondary: { sx: { fontSize: 11 } } }} />
      {i.etiqueta && <Chip size="small" variant="outlined" color="warning" label={i.etiqueta} sx={{ height: 20, fontSize: 10.5, mr: 0.75 }} />}
      {i.chip !== undefined && <Chip size="small" color={color} variant="outlined" label={i.chip} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
      {i.onEliminar && (
        <Tooltip title="Eliminar (solo si no tiene equipos registrados)">
          <IconButton size="small" color="error" aria-label={`Eliminar ${i.texto}`} onClick={(e) => { e.stopPropagation(); i.onEliminar(); }} sx={{ ml: 0.25 }}>
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
  return (
    <>
      {items.length > 7 && (
        <Box sx={{ px: 1, pb: 0.5 }}>
          <TextField fullWidth size="small" placeholder="Filtrar…" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={fieldSx}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
        </Box>
      )}
      <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: "auto", borderTop: 1, borderColor: "divider" }}>
        {visibles.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>{items.length ? "Ninguno coincide con el filtro." : vacio}</Typography>}
        {visibles.map((i) => onElegir ? (
          <ListItemButton
            key={i.key} selected={i.key === seleccionado} onClick={() => onElegir(i.key)}
            sx={{ py: 0.5, "&.Mui-selected, &.Mui-selected:hover": { bgcolor: (t) => alpha(t.palette[color].main, 0.16), boxShadow: (t) => `inset 3px 0 0 ${t.palette[color].main}` } }}
          >{contenido(i)}</ListItemButton>
        ) : (
          <ListItem key={i.key} sx={{ py: 0.5, ...(i.resaltado ? { bgcolor: "success.light" } : {}) }}>{contenido(i)}</ListItem>
        ))}
      </List>
    </>
  );
}

function TabCatalogo() {
  const [servicios, setServicios] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [tiposModelo, setTiposModelo] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [idServicio, setIdServicio] = useState(null);
  const [equipoSel, setEquipoSel] = useState(null); // texto exacto del tipo de equipo
  const [idTipoModelo, setIdTipoModelo] = useState(null);
  const [ultimoId, setUltimoId] = useState(null);
  const [modeloVer, setModeloVer] = useState(null); // modelo cuyos equipos se muestran en el diálogo

  const cargarServicios = useCallback(async () => {
    const { data } = await listarServicios();
    setServicios(data.items);
  }, []);
  const cargarEquipos = useCallback(async () => {
    if (!idServicio) return setEquipos([]);
    const { data } = await listarTiposEquipo({ servicio: idServicio });
    setEquipos(data.items);
  }, [idServicio]);
  const cargarTiposModelo = useCallback(async () => {
    if (!idServicio || equipoSel === null) return setTiposModelo([]);
    const { data } = await listarTiposModelo({ servicio: idServicio, tipo_equipo: equipoSel });
    setTiposModelo(data.items);
  }, [idServicio, equipoSel]);
  const cargarDetalle = useCallback(async () => {
    if (!idTipoModelo) return setDetalle(null);
    const { data } = await getTipoModelo(idTipoModelo);
    setDetalle(data);
  }, [idTipoModelo]);

  useEffect(() => { cargarServicios().catch(() => setServicios([])); }, [cargarServicios]);
  useEffect(() => { cargarEquipos().catch(() => setEquipos([])); }, [cargarEquipos]);
  useEffect(() => { cargarTiposModelo().catch(() => setTiposModelo([])); }, [cargarTiposModelo]);
  useEffect(() => { cargarDetalle().catch(() => setDetalle(null)); }, [cargarDetalle]);

  const { error, aviso, guardando, confirmar, dialogo } = useGuardar(async () => {
    await Promise.all([cargarServicios(), cargarEquipos(), cargarTiposModelo(), cargarDetalle()]);
  });

  const elegirServicio = (id) => { setIdServicio(id); setEquipoSel(null); setIdTipoModelo(null); setUltimoId(null); };
  const elegirEquipo = (valor) => { setEquipoSel(valor); setIdTipoModelo(null); setUltimoId(null); };
  const elegirTipoModelo = (id) => { setIdTipoModelo(id); setUltimoId(null); };

  const agregarEquipo = (nombre) => confirmar("Registrar tipo de equipo", `¿Registrar "${nombre}" como tipo de equipo del servicio ${servicio?.nombre}?`, async () => {
    await crearTipoEquipo({ servicio: idServicio, nombre });
    setEquipoSel(nombre);
    setIdTipoModelo(null);
  }, `Tipo de equipo "${nombre}" registrado.`);
  const agregarTipoModelo = (nombre) => confirmar("Registrar tipo de modelo", `¿Registrar "${nombre}" como tipo de modelo de ${servicio?.nombre} › ${equipo?.nombre}?`, async () => {
    const { data } = await crearTipoModelo({ servicio: idServicio, tipo_equipo: equipoSel, nombre });
    setIdTipoModelo(data.id);
  }, `Tipo de modelo "${nombre}" registrado.`);
  const agregarModelo = (nombre) => confirmar("Registrar modelo", `¿Registrar el modelo "${nombre}" en ${detalle?.nombre} (${servicio?.nombre} › ${equipo?.nombre})?`, async () => {
    const { data } = await crearModelo(idTipoModelo, { nombre });
    setUltimoId(data.id);
  }, `Modelo "${nombre}" registrado.`);

  const OPCIONES_ELIMINAR = { textoConfirmar: "Sí, eliminar", color: "error" };
  const quitarEquipo = (e) => confirmar(
    "Eliminar tipo de equipo",
    `¿Eliminar el tipo de equipo "${e.nombre}" de ${servicio?.nombre}${e.tipos_modelo ? ` junto con sus ${e.tipos_modelo} tipo(s) de modelo y todos sus modelos` : ""}? Solo se elimina si ningún equipo usa esos modelos. No se puede deshacer.`,
    async () => { await eliminarTipoEquipo({ servicio: idServicio, tipo_equipo: e.valor }); if (equipoSel === e.valor) { setEquipoSel(null); setIdTipoModelo(null); } },
    `Tipo de equipo "${e.nombre}" eliminado.`, OPCIONES_ELIMINAR,
  );
  const quitarTipoModelo = (t) => confirmar(
    "Eliminar tipo de modelo",
    `¿Eliminar el tipo de modelo "${t.nombre}"${t.modelos ? ` y sus ${t.modelos} modelo(s)` : ""}? Solo se elimina si ningún equipo usa esos modelos. No se puede deshacer.`,
    async () => { await eliminarTipoModelo(t.id); if (idTipoModelo === t.id) setIdTipoModelo(null); },
    `Tipo de modelo "${t.nombre}" eliminado.`, OPCIONES_ELIMINAR,
  );
  const quitarModelo = (m) => confirmar(
    "Eliminar modelo",
    `¿Eliminar el modelo "${m.nombre.trim()}"? Solo se elimina si ningún equipo está registrado con ese modelo. No se puede deshacer.`,
    async () => { await eliminarModelo(m.id); },
    `Modelo "${m.nombre.trim()}" eliminado.`, OPCIONES_ELIMINAR,
  );

  const servicio = servicios.find((s) => s.id === idServicio);
  const equipo = equipos.find((e) => e.valor === equipoSel);
  const esAccesorio = !!equipo?.accesorio;
  const minusculas = (lista) => lista.map((x) => x.nombre.trim().toLowerCase());

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 1, minHeight: 28 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Estás en:</Typography>
        {servicio ? <Chip size="small" color="primary" icon={<MiscellaneousServicesIcon />} label={servicio.nombre} /> : <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>elige un servicio</Typography>}
        {equipo && <><Typography sx={{ fontSize: 12 }}>›</Typography><Chip size="small" color="info" icon={<DevicesOtherIcon />} label={equipo.nombre} /></>}
        {detalle && <><Typography sx={{ fontSize: 12 }}>›</Typography><Chip size="small" color="secondary" icon={<CategoryIcon />} label={detalle.nombre} /></>}
      </Box>
      <Mensajes error={error} aviso={aviso} />
      {dialogo}
      <EquiposDeModeloDialog modelo={modeloVer} onClose={() => setModeloVer(null)} />
      <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", md: "0.85fr 1fr 1.05fr 1.3fr" } }}>
        <Columna numero={1} titulo="Servicio" ayuda="Elige el servicio" color="primary" icono={MiscellaneousServicesIcon}>
          <ListaItems
            items={servicios.map((s) => ({ key: s.id, texto: s.nombre, secundario: s.descripcion, chip: s.tipos_modelo }))}
            color="primary" icono={MiscellaneousServicesIcon} seleccionado={idServicio} onElegir={elegirServicio} vacio="No hay servicios. Créalos en la pestaña Servicios."
          />
        </Columna>

        <Columna numero={2} titulo="Tipo de equipo" ayuda="Clase de equipo del servicio" color="info" icono={DevicesOtherIcon} bloqueada={!idServicio} mensajeBloqueada="Primero elige un servicio.">
          <AgregarFila key={`eq-${idServicio}`} color="info" etiqueta="Nuevo tipo de equipo…" maxLength={50} existentes={minusculas(equipos)} guardando={guardando} onAgregar={agregarEquipo} />
          <ListaItems
            key={`le-${idServicio}`}
            items={equipos.map((e) => ({ key: e.valor, texto: e.nombre, chip: e.tipos_modelo, etiqueta: e.accesorio ? "por cantidad" : undefined, onEliminar: e.accesorio ? undefined : () => quitarEquipo(e) }))}
            color="info" icono={DevicesOtherIcon} seleccionado={equipoSel} onElegir={elegirEquipo} vacio="Este servicio todavía no tiene tipos de equipo."
          />
        </Columna>

        <Columna numero={3} titulo="Tipo de modelo" ayuda="Agrupa los modelos de ese equipo" color="secondary" icono={CategoryIcon} bloqueada={equipoSel === null} mensajeBloqueada="Primero elige un tipo de equipo.">
          {esAccesorio && <Box sx={{ px: 1, pt: 1 }}><AvisoAccesorio /></Box>}
          <AgregarFila key={`tm-${idServicio}-${equipoSel}`} color="secondary" etiqueta="Nuevo tipo de modelo…" maxLength={40} existentes={minusculas(tiposModelo)} guardando={guardando} onAgregar={agregarTipoModelo} />
          <ListaItems
            key={`lt-${idServicio}-${equipoSel}`}
            items={tiposModelo.map((t) => ({ key: t.id, texto: t.nombre, chip: t.modelos, onEliminar: () => quitarTipoModelo(t) }))}
            color="secondary" icono={CategoryIcon} seleccionado={idTipoModelo} onElegir={elegirTipoModelo} vacio="Todavía no hay tipos de modelo. Registra el primero arriba."
          />
        </Columna>

        <Columna numero={4} titulo="Modelos" ayuda="Haz clic en un modelo para ver sus equipos" color="success" icono={Inventory2Icon} bloqueada={!idTipoModelo || !detalle} mensajeBloqueada="Primero elige un tipo de modelo.">
          <AgregarFila key={`mo-${idTipoModelo}`} color="success" etiqueta="Nuevo modelo…" maxLength={70} existentes={detalle ? minusculas(detalle.modelos) : []} guardando={guardando} onAgregar={agregarModelo} />
          <ListaItems
            key={`lm-${idTipoModelo}`}
            items={(detalle?.modelos || []).map((m) => ({ key: m.id, texto: m.nombre, secundario: m.fecha, resaltado: m.id === ultimoId, onEliminar: () => quitarModelo(m) }))}
            color="success" icono={Inventory2Icon} seleccionado={null}
            onElegir={(id) => { const m = detalle?.modelos.find((x) => x.id === id); if (m) setModeloVer({ id: m.id, nombre: `${detalle.nombre.trim()} ${m.nombre.trim()}`.trim() }); }}
            vacio="Este tipo de modelo todavía no tiene modelos. Registra el primero arriba."
          />
        </Columna>
      </Box>
    </>
  );
}

/* ------------------------------ Servicios ------------------------------ */

function TabServicios({ recargarCatalogos }) {
  const [items, setItems] = useState([]);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [observacion, setObservacion] = useState("");

  const cargar = useCallback(async () => {
    const { data } = await listarServicios();
    setItems(data.items);
  }, []);
  useEffect(() => { cargar().catch(() => setItems([])); }, [cargar]);

  const { error, aviso, guardando, confirmar, dialogo } = useGuardar(async () => { await cargar(); await recargarCatalogos(); });

  const agregar = async () => {
    if (await confirmar("Registrar servicio", `¿Registrar el servicio "${nombre.trim()}"?`, () => crearServicio({ nombre, descripcion, observacion }), "Servicio registrado.")) { setNombre(""); setDescripcion(""); setObservacion(""); }
  };

  return (
    <>
      <Barra>
        <TextField size="small" label="Servicio" value={nombre} onChange={(e) => setNombre(e.target.value)} slotProps={{ htmlInput: { maxLength: 30 } }} sx={{ ...fieldSx, width: 180 }} />
        <TextField size="small" label="Descripción" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} sx={{ ...fieldSx, width: 280 }} />
        <TextField size="small" label="Observación (opcional)" value={observacion} onChange={(e) => setObservacion(e.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} sx={{ ...fieldSx, width: 260 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={guardando || !nombre.trim() || !descripcion.trim()} onClick={agregar}>Registrar</Button>
      </Barra>
      <Mensajes error={error} aviso={aviso} />
      {dialogo}
      <Listado
        columnas={[
          { campo: "nombre", titulo: "Servicio" }, { campo: "descripcion", titulo: "Descripción" }, { campo: "estado", titulo: "Estado" },
          { campo: "tipos_equipo", titulo: "Tipos de equipo", align: "right" }, { campo: "tipos_modelo", titulo: "Tipos de modelo", align: "right" }, { campo: "fecha", titulo: "Registro" },
        ]}
        filas={items}
      />
    </>
  );
}

/* -------------------------------- Fallas -------------------------------- */

function TabFallas({ catalogos, recargarCatalogos }) {
  const [filtro, setFiltro] = useState("");
  const [tipo, setTipo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [items, setItems] = useState([]);

  const cargar = useCallback(async () => {
    const { data } = await listarFallas({ tipo: filtro || undefined });
    setItems(data.items);
  }, [filtro]);
  useEffect(() => { cargar().catch(() => setItems([])); }, [cargar]);

  const { error, aviso, guardando, confirmar, dialogo } = useGuardar(async () => { await cargar(); await recargarCatalogos(); });

  const agregar = async () => {
    if (await confirmar("Registrar falla", `¿Registrar la falla "${descripcion.trim()}" de tipo ${tipo}?`, () => crearFalla({ tipo, descripcion }), "Falla registrada.")) setDescripcion("");
  };

  return (
    <>
      <Barra>
        <TextField select size="small" label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
          {catalogos.tipos_falla.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Descripción de la falla" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} slotProps={{ htmlInput: { maxLength: 50 } }} sx={{ ...fieldSx, width: 300 }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={guardando || !tipo || !descripcion.trim()} onClick={agregar}>Registrar</Button>
        <Box sx={{ flex: 1 }} />
        <TextField select size="small" label="Filtrar por tipo" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
          <MenuItem value="">Todos</MenuItem>
          {catalogos.tipos_falla.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Barra>
      <Mensajes error={error} aviso={aviso} />
      {dialogo}
      <Listado
        columnas={[{ campo: "tipo", titulo: "Tipo" }, { campo: "descripcion", titulo: "Falla" }, { campo: "contador", titulo: "Veces usada", align: "right" }, { campo: "fecha", titulo: "Registro" }]}
        filas={items}
      />
    </>
  );
}

/* --------------------------------- Página --------------------------------- */

// Catálogo de equipos: servicios → tipos de equipo → tipos de modelo → modelos, más las fallas (antes, la pantalla con pestañas del sistema antiguo).
export default function CatalogoEquiposPage() {
  const [tab, setTab] = useState(0);
  const [catalogos, setCatalogos] = useState({ servicios: [], equipos: [], tipos_falla: [] });
  const [error, setError] = useState("");

  const recargarCatalogos = useCallback(async () => {
    const { data } = await getCatalogos();
    setCatalogos(data);
  }, []);

  useEffect(() => {
    recargarCatalogos().catch((e) => setError(mensajeError(e, "No se pudieron cargar los catálogos.")));
  }, [recargarCatalogos]);

  return (
    <DashboardLayout>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Modelos de equipos</Typography>
        {error && <Alert severity="error" sx={{ py: 0, mb: 1 }}>{error}</Alert>}
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab icon={<Inventory2Icon fontSize="small" />} iconPosition="start" label="Catálogo" />
            <Tab icon={<MiscellaneousServicesIcon fontSize="small" />} iconPosition="start" label="Servicios" />
            <Tab icon={<ReportProblemIcon fontSize="small" />} iconPosition="start" label="Fallas" />
          </Tabs>
        </Paper>
        {tab === 0 && <TabCatalogo />}
        {tab === 1 && <TabServicios recargarCatalogos={recargarCatalogos} />}
        {tab === 2 && <TabFallas catalogos={catalogos} recargarCatalogos={recargarCatalogos} />}
      </Box>
    </DashboardLayout>
  );
}
